import React, { useEffect, useState } from 'react';
import { Typography, CircularProgress, Box } from '@mui/material';
import { format } from 'date-fns';
import { apiUrl } from '../../utilities/apiUrl';
import { GeneralModal } from './GeneralModal';
import { ModalButton } from './ModalButton';

export const ViewEmployeeModal = ({ open, onClose, employeeId }) => {
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !employeeId) {
      setEmployeeData(null);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    setEmployeeData(null);

    fetch(apiUrl(`/employee/public/profile/${employeeId}?_t=${Date.now()}`), {
      method: 'GET',
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) {
          let serverMessage = '';
          try {
            const errData = await response.json();
            serverMessage = errData.message;
          } catch (e) {}

          throw new Error(
            `Server returned status ${response.status}. ${serverMessage || 'Failed to fetch employee details.'}`
          );
        }
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          setEmployeeData(data.data);
        } else {
          setError(data.message || 'An error occurred.');
        }
      })
      .catch((err) => {
        console.error(err);
        setError(err.message || 'Server error occurred.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, employeeId]);

  const scrollableFieldStyle = {
    whiteSpace: 'pre-line',
    maxHeight: '100px',
    overflowY: 'auto',

    // Scroll style WebKit browsers
    '&::-webkit-scrollbar': { width: '8px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: '#718096',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': { background: '#A0AEC0' },

    // Scroll style (Firefox)
    scrollbarWidth: 'thin',
    scrollbarColor: '#718096 transparent',
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Specified';
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  return (
    <GeneralModal
      open={open}
      onClose={onClose}
      style={{ maxHeight: '90vh', width: 600 }}
    >
      <Typography
        id="modal-modal-title"
        variant="h6"
        component="h2"
        sx={{ mb: 2 }}
      >
        Employee Profile Details
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress color="secondary" />
        </Box>
      )}

      {error && (
        <Typography color="error" textAlign="center" my={2}>
          {error}
        </Typography>
      )}

      {!loading && !error && employeeData && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Full Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {`${employeeData.firstname} ${employeeData.middlename || ''} ${employeeData.lastname}`.replace(
                /\s+/g,
                ' '
              )}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Nickname
            </Typography>
            <Typography variant="body1">
              {employeeData.nickname || 'N/A'}
            </Typography>
          </Box>

          {/* <Box>
						<Typography variant="caption" color="text.secondary">Start Date (Date Joined)</Typography>
						<Typography variant="body1">
							{formatDate(employeeData.datejoined)}
						</Typography>
					</Box> */}

          <Box>
            <Typography variant="caption" color="text.secondary">
              Skills
            </Typography>
            <Typography variant="body1" sx={scrollableFieldStyle}>
              {employeeData.skills || 'None Listed'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Hobbies
            </Typography>
            <Typography variant="body1" sx={scrollableFieldStyle}>
              {employeeData.hobbies || 'None Listed'}
            </Typography>
          </Box>
        </Box>
      )}
      <ModalButton
        primaryClickHandler={onClose}
        primaryBtnName="Close"
        secondaryStyle={{ display: 'none' }}
      />
    </GeneralModal>
  );
};
